import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ConfirmModal, useToast } from "../../../shared/components/SharedUI";
import { useLang } from "../../../shared/i18n/useLang";
import { overlayClose } from "../../../shared/utils/overlayClose";
import { crearGuarda, editarGuarda, listarGuardas, quitarGuarda } from "../services/seguridadService";

const inp = {
  width: "100%", background: "var(--bg-input)", color: "var(--text-primary)",
  border: "1px solid var(--border-card)", borderRadius: 8, padding: "9px 12px", fontSize: "0.9rem",
};
const th = { padding: "10px 12px", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.4,
  color: "var(--text-secondary)", fontWeight: 700, textAlign: "left" };
const td = { padding: "11px 12px", fontSize: "0.86rem", borderTop: "1px solid var(--border-card)" };
const btnMarca = { background: "var(--brand)", color: "#fff", border: "none", fontWeight: 600 };

function Campo({ label, error, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: "0.8rem", color: "var(--text-secondary)", fontWeight: 600, marginBottom: 4 }}>{label}</label>
      {children}
      {error && <div style={{ color: "#f87171", fontSize: "0.78rem", marginTop: 3 }}>{error}</div>}
    </div>
  );
}

function GuardaModal({ guarda, onClose, onSaved }) {
  const L = useLang();
  const nuevo = !guarda;
  const [f, setF] = useState({
    first_name: guarda?.first_name || "", last_name: guarda?.last_name || "",
    username: guarda?.username || "", password: "", is_active: guarda ? guarda.is_active : true,
  });
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);

  async function guardar() {
    setGuardando(true);
    setErrores({});
    try {
      const datos = nuevo ? f : {
        first_name: f.first_name, last_name: f.last_name, is_active: f.is_active,
        ...(f.password ? { password: f.password } : {}),
      };
      const r = nuevo ? await crearGuarda(datos) : await editarGuarda(guarda.id, datos);
      onSaved(r, nuevo);
    } catch (e) {
      setErrores(e.data && typeof e.data === "object" ? e.data : { detail: e.message });
    } finally {
      setGuardando(false);
    }
  }

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "var(--overlay, rgba(0,0,0,0.65))", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 16 }} {...overlayClose(onClose)}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(460px, 100%)", background: "var(--bg-card)",
        border: "1px solid var(--border-card)", borderRadius: 12, padding: 22 }}>
        <h3 style={{ margin: "0 0 14px", color: "var(--text-primary)" }}>
          <i className="fa-solid fa-user-shield" style={{ color: "var(--brand)", marginRight: 8 }} />
          {nuevo ? L("Nuevo guarda", "New guard") : `${L("Editar", "Edit")} ${guarda.username}`}
        </h3>
        {errores.detail && <div style={{ color: "#f87171", fontSize: "0.85rem", marginBottom: 10 }}>{errores.detail}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Campo label={`${L("Nombre", "First name")} *`} error={errores.first_name}>
            <input style={inp} value={f.first_name} onChange={set("first_name")} />
          </Campo>
          <Campo label={L("Apellidos", "Last name")} error={errores.last_name}>
            <input style={inp} value={f.last_name} onChange={set("last_name")} />
          </Campo>
        </div>
        <Campo label={`${L("Usuario", "Username")} *`} error={errores.username}>
          <input style={{ ...inp, opacity: nuevo ? 1 : 0.6 }} value={f.username} onChange={set("username")}
            disabled={!nuevo} autoComplete="off" placeholder={L("Ej: seguridad.luis", "E.g.: security.luis")} />
        </Campo>
        <Campo label={nuevo ? L("Contraseña * (mínimo 8)", "Password * (at least 8)")
                           : L("Nueva contraseña (dejala vacía para no cambiarla)", "New password (leave empty to keep it)")}
          error={errores.password}>
          <input style={inp} type="password" value={f.password} onChange={set("password")} autoComplete="new-password" />
        </Campo>
        {!nuevo && (
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: "0.86rem", color: "var(--text-primary)", marginBottom: 12 }}>
            <input type="checkbox" checked={f.is_active} onChange={set("is_active")} />
            {L("Activo (puede iniciar sesión)", "Active (can log in)")}
          </label>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>{L("Cancelar", "Cancel")}</button>
          <button type="button" className="btn btn-sm" style={btnMarca} disabled={guardando} onClick={guardar}>
            {guardando ? L("Guardando…", "Saving…") : nuevo ? L("Crear guarda", "Create guard") : L("Guardar cambios", "Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Guardas del condominio: el administrador los crea (usuario y contraseña),
 * los edita, los desactiva o los quita. Cada guarda entra con su usuario desde
 * el celular o la tablet y cae directo en la app de Seguridad.
 */
export default function SeguridadGuardas() {
  const { showToast, toastEl } = useToast();
  const L = useLang();
  const [guardas, setGuardas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(null);     // null | "nuevo" | guarda
  const [quitar, setQuitar] = useState(null);
  const [quitando, setQuitando] = useState(false);

  const cargar = useCallback(() => {
    setCargando(true);
    listarGuardas().then(setGuardas).catch((e) => showToast(e.message, false)).finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function confirmarQuitar() {
    setQuitando(true);
    try {
      await quitarGuarda(quitar.id);
      showToast(L(`${quitar.username} ya no tiene acceso a este condominio.`,
                  `${quitar.username} no longer has access to this condominium.`));
      setQuitar(null);
      cargar();
    } catch (e) {
      showToast(e.message, false);
    } finally {
      setQuitando(false);
    }
  }

  return (
    <>
      <span style={{ color: "var(--text-muted)" }}>
        <Link to="/" style={{ color: "inherit" }}>{L("Inicio", "Home")}</Link> /{" "}
        <strong style={{ color: "var(--text-primary)" }}>{L("Guardas", "Guards")}</strong>
      </span>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ margin: "6px 0 14px" }}>
        <div>
          <h2 className="mt-2 mb-1 p-0 title">{L("Guardas de seguridad", "Security guards")}</h2>
          <p className="m-0" style={{ color: "var(--text-muted)" }}>
            {L("Usuarios que usan la app de Seguridad en este condominio", "Users of the Security app in this condominium")}
          </p>
        </div>
        <button type="button" className="btn d-flex align-items-center gap-2" style={btnMarca} onClick={() => setModal("nuevo")}>
          <i className="fa-solid fa-plus" /> {L("Nuevo guarda", "New guard")}
        </button>
      </div>

      <div style={{ background: "rgba(var(--brand-rgb),0.07)", border: "1px solid rgba(var(--brand-rgb),0.3)", borderRadius: 10,
        padding: "10px 14px", fontSize: "0.84rem", color: "var(--text-secondary)", marginBottom: 14 }}>
        <i className="fa-solid fa-circle-info" style={{ color: "var(--brand)", marginRight: 6 }} />
        {L("El guarda inicia sesión con su usuario desde el celular o la tablet y entra directo a la app de Seguridad. No ve finanzas ni ningún otro módulo. Se puede instalar como app en el celular.",
           "The guard logs in with their username from a phone or tablet and goes straight to the Security app. They don't see finances or any other module. It can be installed as an app on the phone.")}
      </div>

      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            {[L("Guarda", "Guard"), L("Usuario", "Username"), L("Estado", "Status"), ""].map((h, i) => <th key={i} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "var(--text-secondary)" }}>{L("Cargando…", "Loading…")}</td></tr>
            ) : guardas.length === 0 ? (
              <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "var(--text-secondary)" }}>
                {L("Todavía no hay guardas. Creá el primero.", "There are no guards yet. Create the first one.")}
              </td></tr>
            ) : guardas.map((g) => (
              <tr key={g.id}>
                <td style={{ ...td, fontWeight: 700 }}>
                  {`${g.first_name} ${g.last_name}`.trim() || "-"}
                  {g.otros_condominios > 0 && (
                    <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 400 }}>
                      {L(`También trabaja en ${g.otros_condominios} condominio(s) más`,
                         `Also works in ${g.otros_condominios} other condominium(s)`)}
                    </div>
                  )}
                </td>
                <td style={td}>{g.username}</td>
                <td style={td}>
                  <span style={{ color: g.is_active ? "#10b981" : "#f87171", fontWeight: 700 }}>
                    {g.is_active ? L("Activo", "Active") : L("Inactivo", "Inactive")}
                  </span>
                </td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  <button type="button" className="btn btn-sm btn-outline-secondary me-2" disabled={!g.editable}
                    title={g.editable ? L("Editar", "Edit")
                      : L("Trabaja en otros condominios: lo edita el superadministrador",
                          "Works in other condominiums: the super administrator edits it")}
                    onClick={() => setModal(g)}>
                    <i className="fa-solid fa-pen" />
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger" title={L("Quitar de este condominio", "Remove from this condominium")}
                    onClick={() => setQuitar(g)}>
                    <i className="fa-solid fa-user-minus" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <GuardaModal
          guarda={modal === "nuevo" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={(g, nuevo) => {
            setModal(null);
            showToast(nuevo ? L(`Guarda ${g.username} creado.`, `Guard ${g.username} created.`) : L("Cambios guardados.", "Changes saved."));
            cargar();
          }}
        />
      )}
      <ConfirmModal
        open={!!quitar}
        loading={quitando}
        message={quitar && L(
          `¿Quitar a ${quitar.username} de este condominio? Ya no va a poder usar la app de Seguridad aquí. Si no le queda ningún condominio, su usuario se desactiva (la bitácora conserva sus registros).`,
          `Remove ${quitar.username} from this condominium? They won't be able to use the Security app here. If they have no condominiums left, their user is deactivated (the log keeps their records).`)}
        onConfirm={confirmarQuitar}
        onCancel={() => setQuitar(null)}
      />
      {toastEl}
    </>
  );
}
